import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { datasetQueryKeys } from "@/shared/domain/datasets/hooks";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { fileQueryKeys } from "@/shared/domain/files/hooks";

import { CollectionRecord, CollectionResourceRef } from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

interface AddToCollectionVariables {
  collectionId: string;
  resourceOwnerId: string;
  resources: CollectionResourceRef[];
}

export function useAddToCollection() {
  const { collections } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<CollectionRecord, Error, AddToCollectionVariables>({
    mutationFn: ({ collectionId, resourceOwnerId, resources }) =>
      collections.updateCollection(
        collectionId,
        { add_resources: resources },
        { resourceOwnerId },
      ),
    onSuccess: (record, variables) => {
      queryClient.setQueryData(
        collectionQueryKeys.detail(record.collection_id),
        record,
      );
      // Invalidate full resource_type list here because collection lists depend on
      // list query keys. This will be cleaned up later.
      const types = new Set(variables.resources.map((r) => r.resource_type));
      if (types.has("dataset")) {
        void queryClient.invalidateQueries({
          queryKey: datasetQueryKeys.lists(),
        });
      }
      if (types.has("file")) {
        void queryClient.invalidateQueries({ queryKey: fileQueryKeys.lists() });
      }
      if (types.has("event")) {
        void queryClient.invalidateQueries({
          queryKey: eventQueryKeys.lists(),
        });
      }
    },
  });
}
